// Admin › Settings › Scout sign-in: how long a Scout keeps its owner signed in
// to MyPolaris, per role. Empty = until the Scout is reset or they sign out on it.
//
// @docs:
//   - docs/core/architecture/SCOUT_MYPOLARIS_SIGN_IN.md

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ScoutSignInRole, type ScoutSignInSettings } from '../../services/api';

const ROLES: { role: ScoutSignInRole; label: string }[] = [
  { role: 'user', label: 'Customers' },
  { role: 'tester', label: 'Testers' },
  { role: 'admin', label: 'Admins' },
  { role: 'super_admin', label: 'Super admins' },
];

type Draft = Record<ScoutSignInRole, string>;

const toDraft = (s: ScoutSignInSettings): Draft => ({
  user: s.days_by_role.user?.toString() ?? '',
  tester: s.days_by_role.tester?.toString() ?? '',
  admin: s.days_by_role.admin?.toString() ?? '',
  super_admin: s.days_by_role.super_admin?.toString() ?? '',
});

/** '' is never; anything else goes to the API as typed, which checks it. */
const fromDraft = (d: Draft): ScoutSignInSettings => ({
  days_by_role: {
    user: d.user.trim() === '' ? null : Number(d.user),
    tester: d.tester.trim() === '' ? null : Number(d.tester),
    admin: d.admin.trim() === '' ? null : Number(d.admin),
    super_admin: d.super_admin.trim() === '' ? null : Number(d.super_admin),
  },
});

export function ScoutSignInRuleTab() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'scout-sign-in'],
    queryFn: () => api.adminGetScoutSignIn(),
  });
  const [draft, setDraft] = useState<Draft | null>(null);

  useEffect(() => {
    if (data) setDraft(toDraft(data));
  }, [data]);

  const save = useMutation({
    mutationFn: (d: Draft) => api.adminSaveScoutSignIn(fromDraft(d)),
    onSuccess: (saved) => queryClient.setQueryData(['admin', 'scout-sign-in'], saved),
  });

  if (isLoading || (!draft && !error)) {
    return <p className="text-sm text-slate-400">Loading…</p>;
  }
  if (error || !draft || !data) {
    return <p className="text-sm text-red-400">Could not load the Scout sign-in rule. Please try again.</p>;
  }

  const changed = JSON.stringify(draft) !== JSON.stringify(toDraft(data));
  const saveError = save.error as { response?: { data?: { error?: string } } } | null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-white">Scout sign-in</h2>
        <p className="text-sm text-slate-400 mt-1">
          A Scout keeps its owner signed in to MyPolaris until the Scout is reset or they sign out on it.
          Enter a number of days to sign them out sooner. Leave it empty for never.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {ROLES.map(({ role, label }) => (
          <label key={role} className="block">
            <span className="block text-sm font-medium text-slate-300 mb-1">{label}</span>
            <div className="flex items-center gap-2">
              <input
                type="number"
                inputMode="numeric"
                min={1}
                step={1}
                value={draft[role]}
                placeholder="Never"
                onChange={(e) => { setDraft({ ...draft, [role]: e.target.value }); save.reset(); }}
                className="w-32 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
              <span className="text-sm text-slate-400">days</span>
            </div>
          </label>
        ))}
      </div>

      <p className="text-sm text-slate-400">
        A change reaches Scouts that are already signed in within a day.
      </p>

      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => save.mutate(draft)}
          disabled={!changed || save.isPending}
          className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 disabled:bg-slate-600 text-white text-sm font-medium rounded-lg transition-colors"
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        {save.isSuccess && !changed && <span className="text-sm text-green-400">Saved.</span>}
        {saveError && (
          <span className="text-sm text-red-400">{saveError.response?.data?.error ?? 'Could not save. Please try again.'}</span>
        )}
      </div>
    </div>
  );
}

export default ScoutSignInRuleTab;
